import { isDevelopment } from 'flow/utils/app-utils';

// Server `include` query strings for call-record fetches. With a single
// shared call-records store, anything fetched here lands in
// state.callRecordsData.callRecordsUniversal and is reachable from every
// consumer — so the choice is mostly about payload size vs. how likely the
// user is to need the extra data.

// Full set — use when the user is one click away from opening the detail page
// (e.g. the call detail page itself, or related-calls lists that link to it).
// Prefetching `recordings,voicemessages` here avoids a second roundtrip when
// the user navigates.
export const CALL_RECORD_INCLUDES_DETAIL =
    'contact,recordings,voicemessages,apps,tickets';

// Same as the full set but deliberately WITHOUT `tickets` — used by the
// personal (own extension) related calls list.
//
// Do NOT re-add `tickets` here: getExtensionCallRecords ignores the `tickets`
// include server-side (backend bug), so records come back with no
// omniTicketEntityKey even when it was requested. Because the reducer treats
// every field covered by the include as authoritative (see
// FIELDS_DELETABLE_BOUND_TO_INCLUDE_TOKEN below), requesting `tickets` would
// make this refetch CLEAR a ticket key that an earlier detail fetch had
// populated — flipping a record's "Show ticket" action to "Create ticket".
// Omitting the token keeps omniTicketEntityKey out of scope so the existing
// value is preserved. The related list never shows ticket info anyway; the
// detail page refetches it fresh via CALL_RECORD_INCLUDES_DETAIL.
export const CALL_RECORD_INCLUDES_RELATED_EXTENSION =
    'contact,recordings,voicemessages,apps';

// Slim set — used for broad history-list scrolling fetches where the user may
// browse many records without opening any. Keeps payloads small; detail data
// is fetched on demand when a record is opened.
export const CALL_RECORD_INCLUDES_LIST = 'contact,apps,tickets';

// Not an include WE send: the scope of the CallRecord home embeds in a v3 ticket
// event. The record arrives inside the ticket-events response rather than from a
// call-records fetch, but the reducer still needs the scope to know which fields
// that payload is authoritative for.
//
// This describes what the payload actually CARRIES, which is not the same as the
// flags home passes. CallRecordsServiceImpl.getCallRecordForTicket requests
// RECORDINGS, CONTACT and APPS — but `tickets` belongs here too: home skips that
// flag only because its reverse-lookup would be wasted work when it already knows
// the ticket, and stamps omniTicketEntityKey directly instead
// (HomeOmniTicketServiceFacade.mergeCallRecordEvents). The field is delivered
// either way, so the token is claimed to match.
//
// Deliberately WITHOUT `voicemessages`: that one is genuinely gated on the flag
// with no equivalent side-channel, so voiceMessage stays out of scope and keeps
// whatever an earlier richer fetch populated.
export const CALL_RECORD_INCLUDES_TICKET_EVENT =
    'contact,recordings,apps,tickets';

// Maps each include token to the CallRecord fields it covers.
// Used by the reducer so a fetch only overwrites/clears the fields it asked
// for; previously-populated fields outside the include scope are preserved
// across slim refetches. Fields the API always returns when their underlying
// data is also always returned (key, direction, connected, disconnected,
// answered, number, agent, queue, chatSession, conferenceDTO, DTOSubtype,
// additionalAttributes) don't appear here — they always come back in
// `incoming`, so the plain merge handles them and the deletion branch can
// never fire for them.
const FIELDS_DELETABLE_BOUND_TO_INCLUDE_TOKEN: Record<
    string,
    readonly string[]
> = {
    contact: ['contact'],
    recordings: ['recording', 'recordedCall'],
    voicemessages: ['voiceMessage'],
    apps: ['actions'],
    tickets: ['omniTicketEntityKey'],
};

// Fields the backend populates regardless of include scope, but whose
// existence on a record is itself conditional (the field is omitted when
// it doesn't apply, present otherwise). For deletion semantics to work
// — absence in the response means "this record no longer has it" — every
// fetch must treat them as in-scope, even fetches that didn't name any
// include token covering them.
//
// extendedTranscriptionInfo is one example: backend populates it only when
// transcription events exist for the record. Absence in any response means
// there's no transcription — either it never existed, or the underlying data
// was deleted server-side — and the store should reflect that.
//
// transferredFrom is the other: home writes it with no include token involved,
// but only for a call that was attended-transferred. Both record builders set
// it — populateCallRecord (extension and queue paths) and
// createByNumberCallRecord (getCallRecordsByNumber, incoming reading only) — so
// every payload carrying a call record carries the field whenever it applies.
// There is deliberately no `transferredFrom` entry in the token map above —
// the token doesn't exist and fieldsCoveredByIncludes throws on unknown ones.
const FIELDS_DELETABLE_NOT_BOUND_TO_INCLUDE_TOKEN: readonly string[] = [
    'extendedTranscriptionInfo',
    'transferredFrom',
];

const KNOWN_TOKENS = new Set(
    Object.keys(FIELDS_DELETABLE_BOUND_TO_INCLUDE_TOKEN),
);

// Parses an include payload (comma-separated string or array of tokens) into
// the set of CallRecord field names the fetch is authoritative for.
// Unknown tokens throw in development so a typo or a new fetch site that
// forgot to update FIELDS_DELETABLE_BOUND_TO_INCLUDE_TOKEN surfaces immediately; in
// production we degrade gracefully (ignore the unknown token) so a missed
// mapping update can't break end users.
export const fieldsCoveredByIncludes = (
    include: string | readonly string[] | undefined,
): Set<string> => {
    const fields = new Set<string>(FIELDS_DELETABLE_NOT_BOUND_TO_INCLUDE_TOKEN);
    if (!include) return fields;
    const tokens = Array.isArray(include)
        ? include
        : (include as string).split(',');
    for (const rawToken of tokens) {
        const token = rawToken.trim();
        if (!KNOWN_TOKENS.has(token)) {
            if (isDevelopment()) {
                throw new Error(
                    `[call-record-fetch-includes] Unknown include token "${token}". ` +
                        'Add it to FIELDS_DELETABLE_BOUND_TO_INCLUDE_TOKEN or fix the call site.',
                );
            }
            continue;
        }
        for (const field of FIELDS_DELETABLE_BOUND_TO_INCLUDE_TOKEN[token]) {
            fields.add(field);
        }
    }
    return fields;
};
